import { RecordLink } from 'components/record-chip/RecordLink';
import { Checkbox } from 'components/ui/checkbox';
import { Controller, FieldValues, Path, PathValue } from 'react-hook-form';
import { cn } from 'utils/cn';
import { CommonInputProps } from './InputTypes';
import InputWrapper from './InputWrapper';
import { useMemoizedRules } from './useMemoizedRules';

interface CheckboxOption {
    value: string | number;
    label: string;
    disabled?: boolean;
}

interface CheckboxListInputProps<T extends FieldValues> extends Omit<CommonInputProps<T>, 'onChange'> {
    options: CheckboxOption[];
    onChange?: (selectedValues: (string | number)[]) => void;
    readonly?: boolean;
    labelLink?: (value: string | number) => string;
}

/**
 * **CheckboxListInput Component**
 *
 * A reusable React Hook Form compatible component that renders a list of checkboxes.
 *
 * @example
 * ```tsx
 * // Basic usage with invoice selection
 * const invoiceOptions = invoices.map(invoice => ({
 *   value: invoice.id,
 *   label: invoice.invoiceNumber
 * }));
 *
 * <CheckboxListInput
 *   name="selectedInvoices"
 *   control={control}
 *   label="Select Invoices"
 *   options={invoiceOptions}
 *   required
 * />
 * ```
 *
 * @example
 * ```tsx
 * // Usage with custom onChange handler and label link
 * <CheckboxListInput
 *   name="selectedItems"
 *   control={control}
 *   options={[
 *     { value: 1, label: "Option 1" },
 *     { value: 2, label: "Option 2", disabled: true },
 *     { value: 3, label: "Option 3" }
 *   ]}
 *   onChange={(selectedValues) => console.log('Selected:', selectedValues)}
 *   labelLink={(value) => `/invoice/${value}`}
 * />
 * ```
 *
 * @param props - The component props
 * @returns A checkbox list component integrated with react-hook-form
 */
const CheckboxListInput = <T extends FieldValues>({
    name,
    control,
    disabled = false,
    label,
    required = false,
    shouldTranslateLabel = true,
    wrapperClassName,
    validate,
    options,
    onChange,
    readonly = false,
    tooltip,
    labelLink,
}: CheckboxListInputProps<T>) => {
    const rules = useMemoizedRules({ required, validate });
    // See RadioInput: a disabled checkbox would take the failed-submit focus and drop it, hiding
    // every invalid field that comes after this one.
    const focusableIndex = disabled || readonly ? -1 : options.findIndex((option) => !option.disabled);

    return (
        <Controller
            name={name}
            control={control}
            rules={rules}
            render={({ field, fieldState: { error } }) => {
                const selectedValues: (string | number)[] = field.value || [];

                const handleOptionChange = (optionValue: string | number, checked: boolean) => {
                    let newSelectedValues: (string | number)[];

                    if (checked) {
                        // Add the option value if it's not already selected
                        newSelectedValues = selectedValues.includes(optionValue)
                            ? selectedValues
                            : [...selectedValues, optionValue];
                    } else {
                        // Remove the option value
                        newSelectedValues = selectedValues.filter((val) => val !== optionValue);
                    }

                    field.onChange(newSelectedValues as PathValue<T, Path<T>>);

                    if (onChange) {
                        onChange(newSelectedValues);
                    }
                };

                return (
                    <InputWrapper
                        name={name}
                        label={label}
                        required={required}
                        readonly={readonly}
                        shouldTranslateLabel={shouldTranslateLabel}
                        hasErrors={!!error}
                        error={readonly ? undefined : error}
                        tooltip={tooltip}
                        gap={0.25}
                        className={wrapperClassName}
                    >
                        <div className="flex flex-col gap-1">
                            {options.map((option, index) => {
                                const isDisabled = disabled || readonly || option.disabled;
                                return (
                                    <div key={`${option.value}`} className="flex flex-row items-center gap-2">
                                        <Checkbox
                                            ref={index === focusableIndex ? field.ref : undefined}
                                            checked={selectedValues.includes(option.value)}
                                            onCheckedChange={(checked) => handleOptionChange(option.value, checked)}
                                            disabled={isDisabled}
                                            // The kit's Checkbox root is a `span[role="checkbox"]`, which no `<label>`
                                            // can name, so the option text next to it has to be carried explicitly.
                                            aria-label={option.label}
                                            variant="secondary"
                                        />
                                        {labelLink ? (
                                            getLabelLink(option, labelLink)
                                        ) : (
                                            <span
                                                className={cn(
                                                    'text-sm',
                                                    isDisabled
                                                        ? 'text-fg-disabled cursor-default'
                                                        : 'text-fg cursor-pointer'
                                                )}
                                                onClick={() => {
                                                    if (!isDisabled) {
                                                        handleOptionChange(
                                                            option.value,
                                                            !selectedValues.includes(option.value)
                                                        );
                                                    }
                                                }}
                                            >
                                                {option.label}
                                            </span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </InputWrapper>
                );
            }}
        />
    );
};

const getLabelLink = (option: CheckboxOption, getLinkUrl: (value: string | number) => string) => (
    <RecordLink
        withOrigin
        to={getLinkUrl(option.value)}
        label={option.label}
        target="_blank"
        className="text-fg hover:text-link text-sm no-underline hover:underline"
    >
        <div>{option.label}</div>
    </RecordLink>
);

export default CheckboxListInput;
